import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ToastContext, ToastInput, ToastRecord, ToastVariant } from './useToast';

const DEFAULT_DURATION_MS = 4200;
const MAX_VISIBLE_TOASTS = 4;
const TOAST_DEDUPE_WINDOW_MS = 1600;

const variantClasses: Record<ToastVariant, string> = {
  success: 'border-emerald-200 bg-emerald-50 text-emerald-900',
  error: 'border-red-200 bg-red-50 text-red-900',
  info: 'border-blue-200 bg-blue-50 text-blue-900',
};

const variantAccentClasses: Record<ToastVariant, string> = {
  success: 'bg-emerald-500',
  error: 'bg-red-500',
  info: 'bg-blue-500',
};

const createToastId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const recentToastKeysRef = useRef(new Map<string, { id: string; shownAt: number }>());

  const dismissToast = useCallback((id: string) => {
    setToasts(current => current.filter(toast => toast.id !== id));
  }, []);

  const showToast = useCallback((input: ToastInput) => {
    const variant = input.variant ?? 'info';
    const now = Date.now();
    const dedupeKey = `${variant}:${input.message}`;
    const recentToast = recentToastKeysRef.current.get(dedupeKey);
    if (recentToast && now - recentToast.shownAt < TOAST_DEDUPE_WINDOW_MS) {
      return recentToast.id;
    }

    const id = createToastId();
    const toast: ToastRecord = {
      id,
      title: input.title,
      message: input.message,
      variant,
      durationMs: input.durationMs ?? DEFAULT_DURATION_MS,
    };
    recentToastKeysRef.current.set(dedupeKey, { id, shownAt: now });
    setToasts(current => [...current, toast].slice(-MAX_VISIBLE_TOASTS));
    return id;
  }, []);

  const value = useMemo(() => ({ showToast, dismissToast }), [dismissToast, showToast]);

  useEffect(() => {
    if (!toasts.length) return;
    const timers = toasts.map(toast => window.setTimeout(() => dismissToast(toast.id), toast.durationMs));
    return () => timers.forEach(timer => window.clearTimeout(timer));
  }, [dismissToast, toasts]);

  useEffect(() => {
    if (!toasts.length) return;
    const timer = window.setTimeout(() => {
      const cutoff = Date.now() - TOAST_DEDUPE_WINDOW_MS;
      for (const [key, toast] of recentToastKeysRef.current.entries()) {
        if (toast.shownAt < cutoff) recentToastKeysRef.current.delete(key);
      }
    }, TOAST_DEDUPE_WINDOW_MS);
    return () => window.clearTimeout(timer);
  }, [toasts]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {typeof document !== 'undefined' && createPortal(
        <div className="pointer-events-none fixed inset-x-0 top-4 z-[10000] flex flex-col items-center gap-2 px-3 sm:inset-x-auto sm:right-4 sm:items-end" role="status" aria-live="polite">
          {toasts.map(toast => (
            <div
              key={toast.id}
              className={`pointer-events-auto w-full max-w-sm overflow-hidden rounded-xl border shadow-lg backdrop-blur ${variantClasses[toast.variant]}`}
            >
              <div className={`h-1 ${variantAccentClasses[toast.variant]}`} />
              <div className="flex items-start gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  {toast.title && <div className="text-sm font-semibold">{toast.title}</div>}
                  <div className="text-sm">{toast.message}</div>
                </div>
                <button
                  type="button"
                  className="rounded-md px-2 py-1 text-xs font-semibold opacity-70 hover:opacity-100"
                  onClick={() => dismissToast(toast.id)}
                  aria-label="Értesítés bezárása"
                >
                  ×
                </button>
              </div>
            </div>
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
};
